import { useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useSchool } from '@/hooks'
import { getSignInURL } from '@/util/helpers'

import Footer from '@components/Footer'
import StepGuide from '@/components/StepGuide'
import Section from '@/components/Section'
import LoadingScreen from '@/components/LoadingScreen'
import SchoolLogo from '@/components/SchoolLogo'
import Button from '@components/Button'
import LanguagePicker from '@components/LanguagePicker'
import RegistrationHero from '@/components/RegistrationHero'
import RegistrationForm from './Registration/components/RegistrationForm'

import bookblastLogo from '@/assets/bookblast-logo-nav.png'
import bafLogo from '@/assets/baf_logo_no_shadow.png'
import step1 from '@/assets/step1.png'
import step2 from '@/assets/step2.png'
import step3 from '@/assets/step3.png'

export default function WelcomePage() {
  const navigate = useNavigate()
  const { schoolSlug } = useParams()
  const { t } = useTranslation()

  const { data, isLoading, error } = useSchool(schoolSlug)

  useEffect(() => {
    if (error) {
      const url = import.meta.env.VITE_BOOKSAREFUN_URL

      navigate(url, {
        replace: true,
      })
      return
    }
  }, [error, navigate])

  if (isLoading) {
    return <LoadingScreen />
  }

  function navigateToSignIn() {
    const signInURL = getSignInURL(schoolSlug)
    navigate(signInURL)
  }

  const contentWidth = `md:w-5/6 lg:w-4/5 xl:w-3/4 mx-auto`

  return (
    <>
      <title>Welcome - Book Blast by Books Are Fun</title>
      <main className='bg-white'>
        <div id='header' className={`w-full bg-slate-100 font-serif`}>
          <div className='flex flex-row mx-auto pt-4 justify-center w-full'>
            <img className='xs:h-[40px] h-[60px] object-scale-down pr-2' src={bookblastLogo} alt='BookBlast' />
            <div className='text-cta font-serif font-semibold text-md mt-6'>by</div>
            <img className='xs:h-[40px] h-[60px] object-scale-down' src={bafLogo} alt='Books Are Fun' />
          </div>
          <div className='flex flex-row justify-end w-full px-3 md:px-8 pb-2 font-sans'>
            <LanguagePicker />
          </div>
        </div>
        <div id='content' className='flex flex-col w-full  mx-auto grow'>
          <div className='bg-white flex flex-col'>
            <div className={`px-4 md:px-8 pt-6 lg:pt-12 ${contentWidth}`}>
              <h1 className='text-cta text-3xl font-serif text-center font-bold'>
                {t('content.welcome_page.title', { school: data?.schoolName })}
              </h1>
              <div className='mt-4'>
                <SchoolLogo url={data?.schoolLogo ?? undefined} />
              </div>
              <div className='text-cta text-md pt-4 text-center lg:pt-6'>{t('content.welcome_page.subtitle')}</div>
            </div>

            <Section wrapperStyle='bg-cta border-none mt-8 px-4 py-8 pb-10 lg:mt-12 lg:py-12 lg:px-10 !rounded-none'>
              <div className={`flex flex-col lg:flex-row gap-x-12 gap-y-8 ${contentWidth}`}>
                <div className='w-full lg:w-3/5'>
                  <RegistrationHero />
                </div>
                <div className='w-full lg:w-2/5 bg-white p-6 lg:pb-6 rounded-md shadow-md shadow-neutral-900'>
                  {data?.isRegistrationOpen ? (
                    <div>
                      <div className='font-serif font-bold text-2xl pb-0 text-center'>
                        {t('content.components.registration.title')}
                      </div>
                      <RegistrationForm schoolSlug={schoolSlug} />

                      <hr className='my-4 text-neutral-300 mt-6' />
                      <div className='text-sm text-center mb-4'>
                        {t('content.components.registration.existing_account')}
                      </div>
                      <div className='flex flex-row justify-center'>
                        <Link
                          to={getSignInURL(schoolSlug)}
                          className='text-cta text-center hover:text-cta-active hover:underline'
                        >
                          {t('content.components.signin.button_title')}
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <div className='gap-y-8 flex flex-col'>
                      <div className='text-center font-serif text-xl font-bold'>
                        {t('content.components.registration.closed.title')}
                      </div>
                      <div>{t('content.components.registration.closed.subtitle')}</div>
                      <Button
                        variant='cta'
                        label={t('content.components.signin.button_title')}
                        onClick={navigateToSignIn}
                      />
                    </div>
                  )}
                </div>
              </div>
            </Section>

            <Section wrapperStyle='bg-sunshine-yellow !rounded-none'>
              <div className={`${contentWidth}`}>
                <div className='text-center font-serif text-3xl font-bold lg:mb-2'>
                  {t('content.welcome_page.step_guide_title')}
                </div>
                <div className='flex flex-col lg:flex-row '>
                  <div className='flex-1'>
                    <StepGuide
                      image={step1}
                      title={t('content.welcome_page.step_one_title')}
                      content={t('content.welcome_page.step_one_body')}
                    />
                  </div>
                  <div className='flex-1'>
                    <StepGuide
                      image={step2}
                      title={t('content.welcome_page.step_two_title')}
                      content={t('content.welcome_page.step_two_body')}
                    />
                  </div>
                  <div className='flex-1'>
                    <StepGuide
                      image={step3}
                      title={t('content.welcome_page.step_three_title')}
                      content={t('content.welcome_page.step_three_body')}
                    />
                  </div>
                </div>
              </div>
            </Section>
          </div>
        </div>
        <Footer />
      </main>
    </>
  )
}
